/**
 * PRD.267 — Patient Family Allergies & Medications.
 *
 * Pure domain logic for the family-history section: allowed enums, status
 * derivation, and validation. No DB / React dependencies so it is unit-testable
 * and shared across registration, profile, and settings.
 *
 * Family records are family-history CONTEXT only. They are never patient
 * allergies or patient medications and never trigger patient-level alerts.
 */

// ─── Enums ───

export const FAMILY_HISTORY_STATUSES = [
  'NOT_ASKED',
  'UNKNOWN',
  'NONE_KNOWN',
  'HAS_HISTORY',
] as const;
export type FamilyHistoryStatus = (typeof FAMILY_HISTORY_STATUSES)[number];

export const FAMILY_HISTORY_STATUS_DEFAULT: FamilyHistoryStatus = 'NOT_ASKED';

export const FAMILY_RELATIONSHIP_TYPES = [
  'MOTHER',
  'FATHER',
  'PARENT',
  'SPOUSE',
  'CHILD',
  'SON',
  'DAUGHTER',
  'BROTHER',
  'SISTER',
  'SIBLING',
  'GRANDMOTHER',
  'GRANDFATHER',
  'GRANDPARENT',
  'GUARDIAN',
  'CAREGIVER',
  'OTHER',
] as const;
export type FamilyRelationshipType = (typeof FAMILY_RELATIONSHIP_TYPES)[number];

export const FAMILY_ALLERGY_TYPES = ['DRUG', 'FOOD', 'ENVIRONMENTAL', 'OTHER', 'UNKNOWN'] as const;
export type FamilyAllergyType = (typeof FAMILY_ALLERGY_TYPES)[number];

export const FAMILY_ALLERGY_SEVERITIES = ['MILD', 'MODERATE', 'SEVERE', 'UNKNOWN'] as const;
export type FamilyAllergySeverity = (typeof FAMILY_ALLERGY_SEVERITIES)[number];

export const FAMILY_MEDICATION_STATUSES = ['CURRENT', 'PAST', 'UNKNOWN'] as const;
export type FamilyMedicationStatus = (typeof FAMILY_MEDICATION_STATUSES)[number];

// ─── Grouped structure (matches PRD Data Access section) ───

export interface FamilyMemberAllergyInput {
  id?: string;
  allergyType?: string;
  substance: string;
  reaction?: string;
  severity?: string;
  notes?: string;
}

export interface FamilyMemberMedicationInput {
  id?: string;
  name: string;
  dose?: string;
  frequency?: string;
  status?: string;
  notes?: string;
}

export interface FamilyMemberInput {
  id?: string;
  relationshipType: string;
  relationshipLabel?: string;
  name?: string;
  sex?: string;
  birthDate?: string;
  ageText?: string;
  notes?: string;
  allergies: FamilyMemberAllergyInput[];
  medications: FamilyMemberMedicationInput[];
}

export interface FamilyHistoryInput {
  historyStatus: FamilyHistoryStatus;
  notes?: string;
  members: FamilyMemberInput[];
}

// ─── Helpers ───

export function isFamilyHistoryStatus(v: unknown): v is FamilyHistoryStatus {
  return typeof v === 'string' && (FAMILY_HISTORY_STATUSES as readonly string[]).includes(v);
}

/** A member counts as "having history" when it has at least one allergy or medication. */
export function memberHasHistory(member: FamilyMemberInput): boolean {
  return (member.allergies?.length ?? 0) > 0 || (member.medications?.length ?? 0) > 0;
}

export function anyMemberHasHistory(members: FamilyMemberInput[]): boolean {
  return members.some(memberHasHistory);
}

/**
 * Derive the effective section status from the user's explicit selection plus
 * the member data. Adding a member with ≥1 allergy/medication forces
 * HAS_HISTORY. NONE_KNOWN / UNKNOWN are preserved as long as there is no member
 * history; NOT_ASKED is promoted to HAS_HISTORY once history exists.
 */
export function deriveFamilyHistoryStatus(input: FamilyHistoryInput): FamilyHistoryStatus {
  if (anyMemberHasHistory(input.members)) return 'HAS_HISTORY';
  if (input.historyStatus === 'HAS_HISTORY') return 'NOT_ASKED';
  return input.historyStatus;
}

// ─── Validation ───

export interface FamilyValidationError {
  path: string;
  message: string;
}

const REQUIRED = (v: string | undefined | null): boolean => !!v && v.trim().length > 0;

export function validateFamilyAllergy(
  allergy: FamilyMemberAllergyInput,
  path = 'allergy',
): FamilyValidationError[] {
  const errors: FamilyValidationError[] = [];
  if (!REQUIRED(allergy.substance)) {
    errors.push({ path: `${path}.substance`, message: 'Substance / allergen name is required.' });
  }
  return errors;
}

export function validateFamilyMedication(
  med: FamilyMemberMedicationInput,
  path = 'medication',
): FamilyValidationError[] {
  const errors: FamilyValidationError[] = [];
  if (!REQUIRED(med.name)) {
    errors.push({ path: `${path}.name`, message: 'Medication name is required.' });
  }
  return errors;
}

export function validateFamilyMember(
  member: FamilyMemberInput,
  path = 'member',
): FamilyValidationError[] {
  const errors: FamilyValidationError[] = [];
  if (!REQUIRED(member.relationshipType)) {
    errors.push({ path: `${path}.relationshipType`, message: 'Relationship is required.' });
  } else if (member.relationshipType === 'OTHER' && !REQUIRED(member.relationshipLabel)) {
    errors.push({
      path: `${path}.relationshipLabel`,
      message: 'Relationship label is required when relationship is OTHER.',
    });
  }
  member.allergies.forEach((a, i) => {
    errors.push(...validateFamilyAllergy(a, `${path}.allergies[${i}]`));
  });
  member.medications.forEach((m, i) => {
    errors.push(...validateFamilyMedication(m, `${path}.medications[${i}]`));
  });
  return errors;
}

/**
 * Validate the whole Family section.
 *
 * When `required` is true the section is only complete if the user selected
 * NONE_KNOWN or UNKNOWN, OR at least one member has at least one allergy or
 * medication. Per-row validation (relationship / substance / medication name)
 * always applies to whatever rows the user actually added.
 */
export function validateFamilyHistorySection(
  input: FamilyHistoryInput,
  opts: { required: boolean },
): { ok: boolean; errors: FamilyValidationError[] } {
  const errors: FamilyValidationError[] = [];

  input.members.forEach((m, i) => {
    errors.push(...validateFamilyMember(m, `members[${i}]`));
  });

  if (opts.required) {
    const satisfied =
      input.historyStatus === 'NONE_KNOWN' ||
      input.historyStatus === 'UNKNOWN' ||
      anyMemberHasHistory(input.members);
    if (!satisfied) {
      errors.push({
        path: 'family.history',
        message:
          'Family history is required. Select "None known" or "Unknown", or add a family member with at least one allergy or medication.',
      });
    }
  }

  return { ok: errors.length === 0, errors };
}

/** Empty section state for a new registration. */
export function emptyFamilyHistory(): FamilyHistoryInput {
  return { historyStatus: FAMILY_HISTORY_STATUS_DEFAULT, notes: undefined, members: [] };
}
